import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, ButtonBase, GlobalStyles, IconButton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { claimConversation, getChatState, rateAnswer, sendQuestion, subscribe } from './chatStore';
import AnswerText from './AnswerText';
import { FeedbackReasons, FeedbackThumbs } from './AnswerFeedback';
import BearIcon from '../../assets/images/chatbot_bear.svg';

// FloatingBear sits at right/bottom 10 and is 140 tall on desktop, 96 on
// mobile, and its close button pokes out above the head: 24px (20 on mobile)
// with a -10px margin, so the stack tops out at 164 / 116. Stacking above that
// with a 16px gap keeps both usable -- the close button is the easy one to
// cover, and covering it leaves the bear with no way to be dismissed.
const BEAR_CLEARANCE = { desktop: 180, mobile: 132 };
// Where the bear is not shown (pages with the sidebar), the launcher sits in
// the corner, this far from both edges -- as in the design, not flush with them.
const CORNER_GAP = { desktop: 32, mobile: 16 };

const CORPUS_LABELS = {
    faq: 'よくあるご質問',
    documents: '約款・料金表'
};

// Several FAQ entries all read よくあるご質問; one mention per label is enough.
const sourceLabels = (sources) => [...new Set(sources.map((source) => CORPUS_LABELS[source.corpus] ?? source.corpus))];

// Sky blue is the portal's brand colour (focus rings). The panel fill and its
// navy outline come from the widget design (AIbot.ai).
const BRAND = {
    sky: '#6ec8e8',
    panel: '#d7f1ff',
    deep: '#0a4c90'
};

// Wording from the business team's review sheet (2026-10-08).
const WELCOME_TEXT =
    '料金プランやお手続きに関して、ご案内をしております。\n\n' +
    '※恐れ入りますが、お客様個別の情報や詳細なご契約内容についてのご質問はお問合せフォームをご利用ください。';

// The API refuses longer questions (customer-chatbot-api, PPS-348); the
// field stops here so the customer never meets that error.
const MAX_QUESTION_CHARS = 1000;

const GENERIC_ERROR = 'ただいまご案内できません。時間をおいてお試しください。';
// The API is throttled as a whole (PPS-345). A 429 means busy, not broken, and
// retrying shortly is the right advice.
const BUSY_ERROR = 'ただいま混み合っています。少し時間をおいて、もう一度お試しください。';
const EXPIRED_ERROR = 'ログインの有効期限が切れました。お手数ですが、再度ログインしてください。';
const ERROR_TEXT = { busy: BUSY_ERROR, expired: EXPIRED_ERROR, generic: GENERIC_ERROR };

const LAUNCHER_SIZE = { desktop: 72, mobile: 60 };
// The open panel's size before any resizing, and the smallest it can be
// dragged to (desktop). The largest is whatever the window leaves room for.
const PANEL_SIZE = { width: 360, height: 600 };
const PANEL_MIN = { width: 320, height: 400 };
// Kept for as long as the page is open, so closing and reopening the panel
// (or a remount of the layout) keeps the size the customer chose.
let rememberedSize = null;
// Set when a portal link in an answer navigates away (PPS-405), so the panel
// comes back tucked against the edge even if the layout remounts on the way.
let reopenCollapsed = false;
// The invisible strips along the left and top edges that can be dragged.
const RESIZE_HANDLE = 8;
// The strip a collapsed panel leaves on the right edge, as tall as the panel.
// Wide enough for a chevron, so customers see they can bring it back.
const TAB_WIDTH = 24;
const TAB_HOVER_MS = 250;
// The panel stops below the portal header (64px) with a small gap.
const HEADER_CLEARANCE = 80;
const GREETING = 'お困りのことはありませんか？';
// The panel sits this far above the launcher (button + gap).
const PANEL_OFFSET = LAUNCHER_SIZE.desktop + 16;

// Shown between sending and the first token, which is most of the perceived
// wait. Each label follows a status frame the API sends when that step really
// happens (PPS-355) -- never a timer. Showing a step that is not happening, or
// is already over, reads as theatre the moment the customer notices.
const SEARCHING_LABEL = '資料を検索しています';
const statusLabel = (frame) => {
    if (!frame || frame.stage === 'searching') return SEARCHING_LABEL;
    if (frame.stage === 'found') {
        return frame.count > 0 ? `${frame.count}件の資料が見つかりました` : '回答を作成しています';
    }
    return SEARCHING_LABEL;
};
// Kept as the one time-based message: past this the wait is longer than
// usual, and saying so is honest whatever step it is on.
const SLOW_AFTER_MS = 6000;
const SLOW_LABEL = 'もう少しお待ちください';

const AVATAR_SIZE = 28;
// The question bubble's tail pokes this far out on the right. Answers keep the
// same margin, so both end at the same right edge (design team).
const BUBBLE_TAIL = 8;

/** A bot turn: the small bear avatar, then the answer as plain text (no bubble). */
const BotRow = ({ children }) => (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <img src={BearIcon} alt="" width={AVATAR_SIZE} height={AVATAR_SIZE} style={{ flexShrink: 0, marginTop: 2 }} />
        <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', pr: `${BUBBLE_TAIL}px` }}>{children}</Box>
    </Box>
);

/** Three dots that fade in turn. Cheap, and it reads as alive rather than stuck. */
const ThinkingDots = () => (
    <Box component="span" sx={{ ml: 0.5, '& > span': { animation: 'chatDot 1.2s infinite' } }}>
        <Box component="span">.</Box>
        <Box component="span" sx={{ animationDelay: '0.2s !important' }}>
            .
        </Box>
        <Box component="span" sx={{ animationDelay: '0.4s !important' }}>
            .
        </Box>
        <GlobalStyles styles={{ '@keyframes chatDot': { '0%, 60%, 100%': { opacity: 0.25 }, '30%': { opacity: 1 } } }} />
    </Box>
);

const ChatWidget = ({ aboveBear = true }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const bearClearance = isMobile ? BEAR_CLEARANCE.mobile : BEAR_CLEARANCE.desktop;
    const cornerGap = isMobile ? CORNER_GAP.mobile : CORNER_GAP.desktop;
    const launcherSize = isMobile ? LAUNCHER_SIZE.mobile : LAUNCHER_SIZE.desktop;
    const bottom = aboveBear ? bearClearance : cornerGap;

    const [isOpen, setIsOpen] = useState(() => reopenCollapsed);
    // Open but tucked against the right edge after a click elsewhere on the
    // page (desktop only), so it stops covering what the customer is reading.
    const [isCollapsed, setIsCollapsed] = useState(() => reopenCollapsed);
    const panelRef = useRef(null);
    const hoverTimer = useRef(null);
    useEffect(() => () => clearTimeout(hoverTimer.current), []);
    const [size, setSize] = useState(() => rememberedSize ?? PANEL_SIZE);
    const [isResizing, setIsResizing] = useState(false);
    // Ends a drag in progress; also on unmount (logout, a layout remount), so
    // the page is never left unselectable.
    const stopResize = useRef(null);
    useEffect(() => () => stopResize.current?.(), []);
    const close = () => {
        setIsOpen(false);
        setIsCollapsed(false);
    };
    // After a portal link: tuck the panel away on desktop so the page shows,
    // close it on a phone, where it would cover most of the page.
    const onPortalLink = () => {
        if (isMobile) {
            close();
            return;
        }
        reopenCollapsed = true;
        setIsCollapsed(true);
    };
    useEffect(() => {
        if (!isOpen || !isCollapsed) reopenCollapsed = false;
    }, [isOpen, isCollapsed]);
    const [question, setQuestion] = useState('');
    const [isSlow, setIsSlow] = useState(false);

    // The conversation lives in chatStore so that it outlives this component:
    // MainRoutes remounts the layout on some navigations. See chatStore.js.
    const [chat, setChat] = useState(() => {
        claimConversation();
        return getChatState();
    });
    useEffect(() => {
        // The store may have moved on between the first render and this
        // subscription -- an answer still streaming from before a remount.
        setChat(getChatState());
        return subscribe(setChat);
    }, []);
    const { messages, isAnswering, statusFrame } = chat;

    const scrollRef = useRef(null);
    const launcherRef = useRef(null);
    const inputRef = useRef(null);

    // A press anywhere outside the open panel tucks it away. Not on a phone,
    // where the panel is a sheet over most of the screen and there is no
    // hover to bring it back.
    // A press inside the panel's React tree marks itself first, so presses in
    // its portals (the rating tooltips) count as inside too.
    const pressedInside = useRef(false);
    useEffect(() => {
        if (!isOpen || isCollapsed || isMobile) return undefined;
        pressedInside.current = false;
        const onPointerDown = (event) => {
            const inside = pressedInside.current;
            pressedInside.current = false;
            if (inside || event.button !== 0) return;
            setIsCollapsed(true);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [isOpen, isCollapsed, isMobile]);
    // Phones never collapse; a panel tucked away before the window narrowed comes back.
    useEffect(() => {
        if (isMobile) setIsCollapsed(false);
    }, [isMobile]);
    // React 17 has no `inert` prop: set it directly, so the tucked-away panel's
    // controls cannot be tabbed into or read out.
    useEffect(() => {
        if (panelRef.current) panelRef.current.inert = isCollapsed;
    }, [isCollapsed, isOpen]);

    // The launcher is hidden while the panel is open, so focus moves with it:
    // into the question field on open (not on a phone, where that would pop
    // the keyboard over the greeting), back to the launcher on close.
    // Expanding from the tab removes the tab, which had focus, so focus goes
    // into the panel too (after `inert` is lifted, hence this order of effects).
    const wasOpen = useRef(isOpen);
    const wasCollapsed = useRef(isCollapsed);
    useEffect(() => {
        const opened = isOpen && (!wasOpen.current || wasCollapsed.current) && !isCollapsed;
        if (opened && !isMobile) inputRef.current?.focus();
        else if (!isOpen && wasOpen.current) launcherRef.current?.focus();
        wasOpen.current = isOpen;
        wasCollapsed.current = isCollapsed;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- a breakpoint change alone must not move focus
    }, [isOpen, isCollapsed]);

    // Follows what grows the conversation, not every change to it: rating an
    // earlier answer must not jump the panel away from it.
    const lastMessage = messages[messages.length - 1];
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages.length, lastMessage?.text, lastMessage?.error, isOpen]);

    // Only while nothing has been written yet. Once text is streaming the
    // customer can see progress for themselves and the message is noise.
    useEffect(() => {
        if (!isAnswering) {
            setIsSlow(false);
            return undefined;
        }
        const timer = setTimeout(() => setIsSlow(true), SLOW_AFTER_MS);
        return () => clearTimeout(timer);
    }, [isAnswering]);

    const send = () => {
        const text = question.trim();
        if (!text || isAnswering) return;
        setQuestion('');
        sendQuestion(text);
    };

    // The bot's message is pushed empty and filled by the first text frame, so
    // an empty last entry is exactly "sent, nothing back yet".
    const last = messages[messages.length - 1];
    const waitingForFirstToken = isAnswering && last?.role === 'bot' && last.text === '';

    // Read out once an answer is complete. Announcing the visible list instead
    // would read every streamed token as it lands.
    const announcement =
        !isAnswering && last?.role === 'bot'
            ? [last.text, last.error && (ERROR_TEXT[last.error] ?? GENERIC_ERROR)].filter(Boolean).join(' ')
            : '';

    // The polar bear with a headset from the design (AIbot.ai, page 3).
    const launcher = (
        <ButtonBase
            ref={launcherRef}
            aria-label="チャットで質問する"
            onClick={() => setIsOpen(true)}
            sx={{
                position: 'fixed',
                right: aboveBear ? 16 : cornerGap,
                bottom,
                zIndex: 1200,
                width: launcherSize,
                height: launcherSize,
                borderRadius: '50%',
                boxShadow: '0 4px 12px rgba(10,76,144,0.3)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'scale(1.05)' },
                '&.Mui-focusVisible': { outline: `3px solid ${BRAND.sky}`, outlineOffset: '2px' }
            }}
        >
            <img src={BearIcon} alt="" width={launcherSize} height={launcherSize} style={{ display: 'block' }} />
        </ButtonBase>
    );

    // Desktop: the panel floats in the launcher's corner with the same gap, in
    // full view (the team preferred that to the design's edge-docked panel).
    // Where the floating bear is shown it opens above the bear instead. Phone:
    // a sheet across the bottom of the screen.
    const panelRight = aboveBear ? 16 : cornerGap;
    const panelBottom = aboveBear ? bottom + PANEL_OFFSET : cornerGap;
    // The window can shrink after a resize, so the chosen size is also capped here.
    const maxHeight = `calc(100vh - ${panelBottom + HEADER_CLEARANCE}px)`;
    const maxWidth = `calc(100vw - ${panelRight + 16}px)`;
    const panelHeight = isMobile ? '85vh' : `min(${size.height}px, ${maxHeight})`;
    const panelWidth = isMobile ? '100vw' : `min(${size.width}px, ${maxWidth})`;

    // Dragging the left edge widens the panel and the top edge makes it taller:
    // it is anchored to the bottom-right corner, so those are the free sides.
    const startResize = (event, { horizontal, vertical }) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const rect = panelRef.current.getBoundingClientRect();
        const start = { x: event.clientX, y: event.clientY, width: rect.width, height: rect.height };
        const limit = {
            width: window.innerWidth - panelRight - 16,
            height: window.innerHeight - panelBottom - HEADER_CLEARANCE
        };
        const clamp = (value, min, max) => Math.round(Math.max(min, Math.min(value, Math.max(min, max))));
        const onMove = (move) => {
            const next = {
                width: horizontal ? clamp(start.width + start.x - move.clientX, PANEL_MIN.width, limit.width) : start.width,
                height: vertical ? clamp(start.height + start.y - move.clientY, PANEL_MIN.height, limit.height) : start.height
            };
            rememberedSize = next;
            setSize(next);
        };
        const stop = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            document.body.style.userSelect = '';
            stopResize.current = null;
        };
        const onUp = () => {
            stop();
            setIsResizing(false);
        };
        // No text selection on the page while dragging.
        document.body.style.userSelect = 'none';
        setIsResizing(true);
        stopResize.current = stop;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    };
    const resetSize = () => {
        rememberedSize = null;
        setSize(PANEL_SIZE);
    };
    const resizeHandle = (key, cursor, position, axes, extra = {}) => (
        <Box
            key={key}
            aria-hidden
            onPointerDown={(event) => startResize(event, axes)}
            {...extra}
            sx={{ position: 'absolute', zIndex: 1, cursor, touchAction: 'none', ...position }}
        />
    );
    const resizeHandles = !isMobile && [
        resizeHandle('left', 'ew-resize', { left: 0, top: 0, bottom: 0, width: RESIZE_HANDLE }, { horizontal: true }),
        resizeHandle('top', 'ns-resize', { left: 0, right: 0, top: 0, height: RESIZE_HANDLE }, { vertical: true }),
        resizeHandle(
            'corner',
            'nwse-resize',
            // No bigger than the inset of the close button, so it never covers it.
            { left: 0, top: 0, width: RESIZE_HANDLE, height: RESIZE_HANDLE },
            { horizontal: true, vertical: true },
            // Back to the starting size.
            { onDoubleClick: resetSize, title: 'ダブルクリックで元のサイズに戻す' }
        )
    ];

    const panel = isOpen && (
        <Box
            ref={panelRef}
            onPointerDownCapture={() => {
                pressedInside.current = true;
            }}
            role="dialog"
            aria-label="AIチャット"
            aria-hidden={isCollapsed || undefined}
            sx={{
                position: 'fixed',
                // Collapsing slides the whole panel past the right edge; the tab
                // below stands in for it. Sliding (not unmounting) keeps the
                // scroll position, the answer still streaming and the draft.
                transform: isCollapsed ? `translateX(calc(100% + ${panelRight}px))` : 'none',
                transition: isResizing ? 'none' : 'transform 0.3s ease',
                right: isMobile ? 0 : panelRight,
                bottom: isMobile ? 0 : panelBottom,
                zIndex: 1200,
                width: panelWidth,
                height: panelHeight,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: BRAND.panel,
                border: `2px solid ${BRAND.deep}`,
                ...(isMobile && { borderRight: 'none', borderBottom: 'none', borderLeft: 'none' }),
                borderRadius: isMobile ? '16px 16px 0 0' : '16px',
                boxShadow: '0 8px 24px rgba(10,76,144,0.2)',
                overflow: 'hidden'
            }}
        >
            {resizeHandles}
            <Box sx={{ px: 1, pt: 1 }}>
                <IconButton size="small" onClick={close} aria-label="閉じる" sx={{ color: BRAND.deep }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 1.5 }}>
                {messages.length === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
                        <img src={BearIcon} alt="" width={88} height={88} />
                        <Typography sx={{ mt: 1.5, fontSize: 16, fontWeight: 700, color: BRAND.deep }}>{GREETING}</Typography>
                        {/* Black and justified to both edges, as the design team asked. */}
                        <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                                mt: 2,
                                alignSelf: 'stretch',
                                whiteSpace: 'pre-line',
                                fontSize: 13,
                                textAlign: 'justify',
                                textJustify: 'inter-character'
                            }}
                        >
                            {WELCOME_TEXT}
                        </Typography>
                    </Box>
                )}

                {messages.map((message, index) => {
                    // An empty bot bubble is the placeholder the status line
                    // replaces; drawing both would show two boxes for one turn.
                    if (message.role === 'bot' && message.text === '' && !message.error) return null;
                    const errorText = message.error && (ERROR_TEXT[message.error] ?? GENERIC_ERROR);

                    if (message.role === 'user') {
                        // The customer's own text is shown as typed, in a white
                        // bubble with a tail toward the right, as in the design.
                        return (
                            <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', pr: `${BUBBLE_TAIL}px` }}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        px: 2,
                                        py: 1.25,
                                        maxWidth: '85%',
                                        borderRadius: '12px',
                                        backgroundColor: '#fff',
                                        whiteSpace: 'pre-wrap',
                                        // Long URLs and numbers wrap inside the bubble.
                                        overflowWrap: 'anywhere',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            right: -BUBBLE_TAIL,
                                            top: 'calc(50% - 6px)',
                                            borderLeft: '9px solid #fff',
                                            borderTop: '6px solid transparent',
                                            borderBottom: '6px solid transparent'
                                        }
                                    }}
                                >
                                    <Typography variant="body2">{message.text}</Typography>
                                </Box>
                            </Box>
                        );
                    }

                    // Only a complete answer has an id (from its done frame) to rate.
                    const rateable = Boolean(message.answerId && !message.failed);
                    const rate = (rating, reason) => rateAnswer(index, rating, reason);
                    return (
                        <BotRow key={index}>
                            {message.text && <AnswerText text={message.text} onPortalLink={onPortalLink} />}
                            {/* After any text that did arrive, so a cut-off
                                answer never reads as a complete one. */}
                            {errorText && (
                                <Typography variant="body2" color="error" sx={{ mt: message.text ? 1 : 0 }}>
                                    {errorText}
                                </Typography>
                            )}
                            {(message.sources?.length > 0 || rateable) && (
                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.5 }}>
                                    {/* Plain text, not chips: they cannot be opened, and a chip
                                        reads as a button next to the reason chips. */}
                                    {message.sources?.length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            参照：{sourceLabels(message.sources).join('・')}
                                        </Typography>
                                    )}
                                    {rateable && <FeedbackThumbs feedback={message.feedback} onRate={rate} color={BRAND.deep} />}
                                </Box>
                            )}
                            {rateable && <FeedbackReasons feedback={message.feedback} onRate={rate} color={BRAND.deep} />}
                        </BotRow>
                    );
                })}

                {isAnswering && waitingForFirstToken && (
                    <BotRow>
                        <Box role="status" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {isSlow ? SLOW_LABEL : statusLabel(statusFrame)}
                            </Typography>
                            <ThinkingDots />
                        </Box>
                    </BotRow>
                )}
            </Box>

            <Box sx={{ px: 1.5, pt: 1, pb: 1.5, display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    value={question}
                    placeholder="ご質問を入力してください"
                    onChange={(event) => setQuestion(event.target.value)}
                    inputRef={inputRef}
                    inputProps={{ maxLength: MAX_QUESTION_CHARS }}
                    // Only near the limit, so a pasted text cut at the limit is not a surprise.
                    helperText={question.length >= MAX_QUESTION_CHARS * 0.9 ? `${question.length}/${MAX_QUESTION_CHARS}` : undefined}
                    sx={{
                        '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '8px' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.deep }
                    }}
                    onKeyDown={(event) => {
                        // Shift+Enter keeps a newline; Enter alone sends. Not
                        // while an IME is composing: in Japanese input that
                        // Enter confirms the kana-to-kanji conversion, and
                        // sending on it would post half a question. Safari ends
                        // the composition *before* that keydown, so isComposing
                        // is already false there; keyCode 229 still marks it.
                        const composing = event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
                        if (event.key === 'Enter' && !event.shiftKey && !composing) {
                            event.preventDefault();
                            send();
                        }
                    }}
                />
                <IconButton onClick={send} disabled={!question.trim() || isAnswering} aria-label="送信" sx={{ color: BRAND.deep }}>
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );

    // What is left of a collapsed panel: a navy strip on the right edge where the
    // panel was, as in the design, plus a chevron in the middle that tells
    // customers it opens. Resting on it (or a click, or Enter) brings it back;
    // the short delay keeps a pointer passing by on its way to the scrollbar
    // from reopening it.
    const tab = isOpen && isCollapsed && (
        <ButtonBase
            aria-label="チャットを開く"
            onClick={() => setIsCollapsed(false)}
            onMouseEnter={() => {
                hoverTimer.current = setTimeout(() => setIsCollapsed(false), TAB_HOVER_MS);
            }}
            onMouseLeave={() => clearTimeout(hoverTimer.current)}
            sx={{
                position: 'fixed',
                right: 0,
                bottom: panelBottom,
                zIndex: 1200,
                width: TAB_WIDTH,
                height: panelHeight,
                borderRadius: '12px 0 0 12px',
                backgroundColor: BRAND.deep,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(10,76,144,0.3)',
                '&.Mui-focusVisible': { outline: `3px solid ${BRAND.sky}`, outlineOffset: '2px' }
            }}
        >
            <ChevronLeftIcon />
        </ButtonBase>
    );

    // Outside the panel, so an answer that finishes while it is tucked away
    // (aria-hidden) is still read out.
    const liveRegion = isOpen && (
        <Box
            aria-live="polite"
            sx={{ position: 'fixed', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
        >
            {announcement}
        </Box>
    );

    return createPortal(
        <>
            {panel}
            {liveRegion}
            {tab}
            {!isOpen && launcher}
        </>,
        document.body
    );
};

export default ChatWidget;
